import { type FC } from "react";
import { Briefcase, Info, Mail, MapPin, Phone } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import type { LawyerPreviewData } from "../LawyerSettingsPage";

interface ProfilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  isDirty?: boolean;
  data: LawyerPreviewData;
}

function formatLicensedStates(states: { state: string; bar_number: string }[]): string | null {
  const names = states.map((s) => s.state).filter(Boolean);
  if (names.length === 0) return null;
  if (names.length === 1) return `Licensed in ${names[0]}`;
  if (names.length === 2) return `Licensed in ${names[0]} and ${names[1]}`;
  const last = names[names.length - 1];
  const rest = names.slice(0, -1).join(", ");
  return `Licensed in ${rest}, and ${last}`;
}

export const ProfilePreviewModal: FC<ProfilePreviewModalProps> = ({
  open,
  onClose,
  onSave,
  isDirty = false,
  data,
}) => {
  const displayName = [data.title !== "none" ? data.title : null, data.firstName, data.lastName]
    .filter(Boolean)
    .join(" ");

  const initials = [data.firstName?.[0], data.lastName?.[0]].filter(Boolean).join("").toUpperCase();

  const licensedLine = formatLicensedStates(data.licensedStates ?? []);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>How clients see you</DialogTitle>
          <DialogDescription>Here's how your profile appears to clients</DialogDescription>
        </DialogHeader>

        <div className="bg-subtle rounded-lg p-6 flex flex-col gap-4">
          <div className="flex gap-4 items-center">
            <div className="shrink-0 h-[100px] w-[100px] rounded-full bg-green-950 flex items-center justify-center overflow-hidden">
              {data.photoUrl ? (
                <img
                  src={data.photoUrl}
                  alt={`${displayName} profile photo`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-[30px] font-semibold text-white leading-none">
                  {initials}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                {displayName && <h4>{displayName}</h4>}
                {data.roleTitle && (
                  <Tag color="white" icon={Briefcase}>
                    {data.roleTitle}
                  </Tag>
                )}
              </div>
              {data.firmName && <p className="text-sm text-secondary">{data.firmName}</p>}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {data.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
                <p className="text-sm">{data.email}</p>
              </div>
            )}
            {data.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
                <p className="text-sm">{data.phone}</p>
              </div>
            )}
            {licensedLine && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
                <p className="text-sm">{licensedLine}</p>
              </div>
            )}
          </div>
        </div>

        {isDirty && (
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-warning" aria-hidden="true" />
            <p className="text-sm font-medium text-warning">
              You have unsaved changes. Save to make this version visible to clients.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          {isDirty ? (
            <>
              <Button variant="outline" size="md" onClick={onClose}>
                Done
              </Button>
              <Button variant="brand" size="md" onClick={onSave}>
                Save changes
              </Button>
            </>
          ) : (
            <Button variant="brand" size="md" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
